'use client';

import { useState } from 'react';
import type { ReactNode } from 'react';
import { getCookie } from 'cookies-next';
import { Tag } from '@energy-retailer/icons';
import { useJoinFormStore } from '@/stores/join-form-store/join-form-store';
import MessageBox from '@/app/components/alert/message-box';
import { CartConflictModal } from '@/app/components/cart-conflict-modal';
import StructuredDataScript from '@/app/components/structured-data';
import { getHasCartConflicts } from '@/app/lib/utils/get-has-cart-conflicts';
import { HeroBasic } from '@/components/hero/basic';
import { ProductPlanListing } from '@/components/product-plan-listing';
import type { StructuredData } from '@/app/lib/hooks/get-structured-data/structured-data.types';
import type { Page as PageType } from '@/generated/graphql';
import { PROMO_ID_COOKIE } from '@/constants';

type PageContentProps = {
  ContentBlocks?: ReactNode;
  PlanPrePositionMessage?: ReactNode;
  page: PageType;
  structuredData?: StructuredData;
};
const SWITCH_YOUR_MATES = '987';
const SWITCH_YOUR_MATES_TITLE = 'Switch Your Mates offer';
const SWITCH_YOUR_MATES_DESCRIPTION = (
  <p>
    The lowest possible price in the first year will be $75 less than the
    estimated annual price, with a one-off credit of $75 on your first
    electricity bill if you&apos;re eligible.{' '}
    <a
      href="https://www.powershop.com.au/switch-your-mates-terms-and-conditions"
      target="_blank"
      rel="noreferrer"
    >
      Terms and Conditions apply
    </a>
    .
  </p>
);

const PageContent = ({
  ContentBlocks,
  page,
  structuredData,
  PlanPrePositionMessage,
}: PageContentProps) => {
  const { electricityPlanSlug } = useJoinFormStore();
  const promoId = getCookie(PROMO_ID_COOKIE);

  const [isCartConflictModalOpen, setIsCartConflictModalOpen] =
    useState<boolean>(
      promoId !== SWITCH_YOUR_MATES &&
        getHasCartConflicts({
          // @ts-expect-error -- unawaited promised cookie value - promoId is picked up on the clientside
          promoId,
          electricityPlanSlug,
        })
    );

  return (
    <>
      {structuredData ? <StructuredDataScript data={structuredData} /> : null}
      <div className="flex flex-col gap-b bg-gray-50">
        <HeroBasic
          title={page.title}
          description={page.description}
          paddingBottom={false}
        />
        <ProductPlanListing PlanPrePositionMessage={PlanPrePositionMessage} />
        {promoId === SWITCH_YOUR_MATES && (
          <div className="bg-transparent lg:container">
            <div className="grid grid-cols-1 gap-a max-lg:container md:grid-cols-6 lg:grid-cols-12">
              <div className="col-span-1 sm:col-span-10 sm:col-start-2">
                <MessageBox
                  header={SWITCH_YOUR_MATES_TITLE}
                  icon={<Tag className="mr-a h-icon w-icon text-pink" />}
                  className="box-shadow rounded-rad-b"
                >
                  {SWITCH_YOUR_MATES_DESCRIPTION}
                </MessageBox>
              </div>
            </div>
          </div>
        )}

        <CartConflictModal
          isModalOpen={isCartConflictModalOpen}
          setIsModalOpen={setIsCartConflictModalOpen}
        />

        {ContentBlocks}
      </div>
    </>
  );
};

export default PageContent;
